import React from 'react';
import { cn } from '../../../lib/utils';
import { getEffortBucket } from '../../../design-system/effortScale';

/**
 * EffortDot — planning-consequence atom for DataAwareDatePicker (PRD §4.13).
 *
 * States (visible date behaviour is uniform across in-month and adjacent-month cells):
 *  - `loading`  : skeleton shimmer dot (month stats not yet fetched)
 *  - `empty`    : outline dot (loaded, zero effort that day)
 *  - `loaded`   : solid dot, colour from `DEFAULT_EFFORT_SCALE` bucket
 *  - `error`    : dashed ring (fetch failed for this day)
 *
 * Size: xs (4px, in calendar grid) · sm (7px, default) · md (10px, legend).
 */
const SIZE_CLASSES = {
  xs: 'w-1 h-1',
  sm: 'w-[7px] h-[7px]',
  md: 'w-2.5 h-2.5',
};

export function EffortDot({ stats, state, size = 'sm', className, ...rest }) {
  const resolvedState =
    state || (!stats ? 'loading' : stats.total_effort === 0 ? 'empty' : 'loaded');

  if (resolvedState === 'loading') {
    return (
      <span
        className={cn(
          SIZE_CLASSES[size] || SIZE_CLASSES.sm,
          'inline-block rounded-full animate-pulse',
          className,
        )}
        style={{ backgroundColor: 'var(--orka-surface-subtle)' }}
        aria-hidden="true"
        data-testid="effort-dot-loading"
        {...rest}
      />
    );
  }

  if (resolvedState === 'empty') {
    return (
      <span
        className={cn(
          SIZE_CLASSES[size] || SIZE_CLASSES.sm,
          'inline-block rounded-full border',
          className,
        )}
        style={{ borderColor: 'var(--orka-border-default)' }}
        aria-hidden="true"
        data-testid="effort-dot-empty"
        {...rest}
      />
    );
  }

  if (resolvedState === 'error') {
    return (
      <span
        className={cn(
          SIZE_CLASSES[size] || SIZE_CLASSES.sm,
          'inline-block rounded-full border border-dashed',
          className,
        )}
        style={{ borderColor: 'var(--orka-text-danger)' }}
        aria-hidden="true"
        data-testid="effort-dot-error"
        {...rest}
      />
    );
  }

  const bucket = getEffortBucket(stats?.total_effort || 0);
  return (
    <span
      className={cn(SIZE_CLASSES[size] || SIZE_CLASSES.sm, 'inline-block rounded-full', className)}
      style={{ backgroundColor: bucket.color }}
      aria-hidden="true"
      data-testid="effort-dot-loaded"
      data-bucket={bucket.key}
      {...rest}
    />
  );
}

export default EffortDot;
